import React, { useMemo } from "react";
import {
  Form,
  ModalBody,
  Label,
  FormFeedback,
  Input,
  Spinner,
  Row,
  Col,
  Alert,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import SelectAgency from "./SelectAgency";
import SelectRegion from "./SelectRegion";
import SelectIndividual from "./SelectIndividual";
import SelectPositionType from "./SelectPositionType";
import SelectPaymentCategory from "./SelectPaymentCategory";
import SelectMDA from "./SelectMDA";
import SelectThematicArea from "./SelectThematicArea";
import SelectSector from "./SelectSector";
import { isAllowed } from "common/lib/permissionUtils";
import { useProfile } from "Components/Hooks/UserHooks";

const PaymentForm: React.FC<{
  toggle: () => void;
  loading: boolean;
  isUpdate?: boolean;
  validation: any;
  fieldErrors: any;
}> = ({ validation, toggle, isUpdate, loading, fieldErrors }) => {
  const { userProfile } = useProfile();

  const canSelectAgency = useMemo(() => {
    return isAllowed(userProfile?.permissions, [
      {
        resource: "payment",
        action: "create",
      },
    ]);
  }, [userProfile?.permissions]);

  return (
    <Form
      className="tablelist-form"
      onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
        return false;
      }}
    >
      <ModalBody>
        <input type="hidden" id="id-field" />

        {fieldErrors.length > 0 && (
          <div className="mb-3">
            <Alert color="danger" className="material-shadow">
              {fieldErrors.map((error: any, index: any) => (
                <div key={index}>
                  <strong>{error.field}:</strong> {error.message}
                </div>
              ))}
            </Alert>
          </div>
        )}

        <div className="mb-3">
          <SelectIndividual validation={validation} />
        </div>
        <Row>
          <Col md={6} className="mb-3">
            <Label htmlFor="id-field" className="form-label">
              Position Title
            </Label>
            <Input
              name="positionTitle"
              id="positionTitle"
              className="form-control"
              placeholder="Enter postion title"
              type="text"
              validate={{
                required: { value: true },
              }}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.positionTitle || ""}
              invalid={
                validation.touched.positionTitle &&
                  validation.errors.positionTitle
                  ? true
                  : false
              }
            />
            {validation.touched.positionTitle &&
              validation.errors.positionTitle ? (
              <FormFeedback type="invalid">
                {validation.errors.positionTitle}
              </FormFeedback>
            ) : null}
          </Col>
          <Col md={6} className="mb-3">
            <SelectPositionType validation={validation} />
          </Col>
        </Row>
        <Row>
          <Col md={6} className="mb-3">
            <SelectRegion validation={validation} />
          </Col>
          <Col md={6} className="mb-3">
            <SelectMDA validation={validation} />
          </Col>
          <Col md={6} className="mb-3">
            <SelectPaymentCategory validation={validation} />
          </Col>
          <Col md={6} className="mb-3">
            <Label htmlFor="id-field" className="form-label">
              Project
            </Label>
            <Input
              name="projectOrFund"
              id="projectOrFund"
              className="form-control"
              placeholder="Enter project"
              type="text"
              validate={{
                required: { value: true },
              }}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.projectOrFund || ""}
              invalid={
                validation.touched.projectOrFund &&
                  validation.errors.projectOrFund
                  ? true
                  : false
              }
            />
            {validation.touched.projectOrFund &&
              validation.errors.projectOrFund ? (
              <FormFeedback type="invalid">
                {validation.errors.projectOrFund}
              </FormFeedback>
            ) : null}
          </Col>
          <Col md={12} className="mb-3">
            <Label htmlFor="id-field" className="form-label">
              Donor/Fund
            </Label>
            <Input
              name="donorOrFund"
              id="donorOrFund"
              className="form-control"
              placeholder="Enter donor/fund"
              type="text"
              // validate={{
              //   required: { value: true },
              // }}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.donorOrFund || ""}
              invalid={
                validation.touched.donorOrFund &&
                  validation.errors.donorOrFund
                  ? true
                  : false
              }
            />
            {validation.touched.donorOrFund &&
              validation.errors.donorOrFund ? (
              <FormFeedback type="invalid">
                {validation.errors.donorOrFund}
              </FormFeedback>
            ) : null}
          </Col>
        </Row>

        <Row>
          <Col md={4} className="mb-3">
            <Label htmlFor="id-field" className="form-label">
              Staff ID
            </Label>
            <Input
              name="staffId"
              id="staffId"
              className="form-control"
              placeholder="Enter staff ID"
              type="text"
              validate={{
                required: { value: true },
              }}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.staffId || ""}
              invalid={
                validation.touched.staffId && validation.errors.staffId
                  ? true
                  : false
              }
            />
            {validation.touched.staffId && validation.errors.staffId ? (
              <FormFeedback type="invalid">
                {validation.errors.staffId}
              </FormFeedback>
            ) : null}
          </Col>
          <Col md={4} className="mb-3">
            <Label htmlFor="startDate-field" className="form-label">
              Start Date
            </Label>
            {/* @ts-ignore */}
            <Flatpickr
              name="startDate"
              id="startDate-field"
              className="form-control"
              placeholder="Select start date"
              options={{
                enableTime: false,
                altInput: false,
                altFormat: "d M, Y",
                dateFormat: "d M, Y",
              }}
              onChange={(e) => {
                validation.setFieldValue("startDate", e[0]);
              }}
              value={validation.values.startDate || ""}
            />
            {validation.touched.startDate && validation.errors.startDate ? (
              <FormFeedback type="invalid">
                {validation.errors.startDate}
              </FormFeedback>
            ) : null}
          </Col>
          <Col md={4} className="mb-3">
            <Label htmlFor="dateOfPayment-field" className="form-label">
              End Date
            </Label>
            {/* @ts-ignore */}
            <Flatpickr
              name="endDate"
              id="endDate-field"
              className="form-control"
              placeholder="Select end date"
              options={{
                enableTime: false,
                altInput: false,
                altFormat: "d M, Y",
                dateFormat: "d M, Y",
              }}
              onChange={(e) => {
                validation.setFieldValue("endDate", e[0]);
              }}
              value={validation.values.endDate || ""}
            />
            {validation.touched.endDate && validation.errors.endDate ? (
              <FormFeedback type="invalid">
                {validation.errors.endDate}
              </FormFeedback>
            ) : null}
          </Col>
        </Row>
        <Row>
          <Col md={6} className="mb-3">
            <SelectThematicArea validation={validation} />
          </Col>
          <Col md={6} className="mb-3">
            <SelectSector validation={validation} />
          </Col>
        </Row>

        <Row>
          <Col md={6} className="mb-3">
            <Label htmlFor="id-field" className="form-label">
              Amount Per Month (USD)
            </Label>
            <Input
              name="amountPerMonth"
              id="amountPerMonth"
              className="form-control"
              placeholder="Enter amount per month"
              type="number"
              validate={{
                required: { value: true },
              }}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.amountPerMonth || ""}
              invalid={
                validation.touched.amountPerMonth &&
                  validation.errors.amountPerMonth
                  ? true
                  : false
              }
            />
            {validation.touched.amountPerMonth &&
              validation.errors.amountPerMonth ? (
              <FormFeedback type="invalid">
                {validation.errors.amountPerMonth}
              </FormFeedback>
            ) : null}
          </Col>
          <Col md={6} className="mb-3">
            <div className="form-check form-switch form-switch-lg px-0 form-check-right">
              <Input
                name="isSalaryAligned"
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="flexSwitchCheckCheckedDisabled2"
                value={validation.values.isSalaryAligned}
                onChange={validation.handleChange}
                checked={validation.values.isSalaryAligned}
              />
              <Label
                className="form-check-label"
                htmlFor="flexSwitchCheckCheckedDisabled2"
              // style={{ width: "320px" }}
              >
                Is Salary Aligned with Harmonized Scale?
              </Label>
            </div>
          </Col>
        </Row>
        <Row>
          {canSelectAgency && (
            <Col md={6} className="mb-3">
              <SelectAgency validation={validation} />
            </Col>
          )}
          <Col md={6} className="mb-3">
            <Label htmlFor="id-field" className="form-label">
              Data provided by
            </Label>
            <Input
              name="dataProvidedBy"
              id="dataProvidedBy"
              className="form-control"
              placeholder="Enter data provided by"
              type="text"
              validate={{
                required: { value: true },
              }}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.dataProvidedBy || ""}
              invalid={
                validation.touched.dataProvidedBy &&
                  validation.errors.dataProvidedBy
                  ? true
                  : false
              }
            />
            {validation.touched.dataProvidedBy &&
              validation.errors.dataProvidedBy ? (
              <FormFeedback type="invalid">
                {validation.errors.dataProvidedBy}
              </FormFeedback>
            ) : null}
          </Col>
        </Row>
      </ModalBody>

      <div className="modal-footer">
        <div className="hstack gap-2 justify-content-end">
          <button
            type="button"
            className="btn btn-light"
            onClick={() => {
              toggle();
            }}
          >
            Close
          </button>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading && (
              <Spinner
                color="light"
                size="sm"
                style={{ marginRight: "8px", marginBottom: "-1px" }}
              ></Spinner>
            )}
            {isUpdate ? "Update Payment" : " Add Payment"}
          </button>
        </div>
      </div>
    </Form>
  );
};

export default PaymentForm;
